import bellIcon from "@loot-os/icons/icons/bell";
import { type ReactNode, useId } from "react";

import { Button } from "../../core/Button.js";
import { Icon } from "../../core/Icon.js";
import { LoadMore } from "../../core/LoadMore.js";
import { Skeleton } from "../../core/Skeleton.js";
import { NotificationRow } from "./NotificationRow.js";
import type { NotificationGroupContent, NotificationListContent } from "./notificationTypes.js";

export interface NotificationListProps {
  content: NotificationListContent;
  /** Read beside each unread dot. */
  unreadLabel?: string;
}

/**
 * One tab of the notification panel: rows under their day headings, or why there are none. It
 * scrolls inside whatever height the panel leaves it.
 */
export function NotificationList({ content, unreadLabel }: NotificationListProps) {
  if (content.status === "loading") {
    return (
      <div aria-busy="true" className="flex flex-col gap-1 px-4 py-3">
        <span className="sr-only">{content.label}</span>
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-start gap-3 py-2.5">
            <Skeleton className="size-9 shrink-0 rounded-lg" />
            <div className="flex flex-1 flex-col gap-2 pt-0.5">
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-3 w-full" />
            </div>
            <Skeleton className="h-3 w-6" />
          </div>
        ))}
      </div>
    );
  }
  if (content.status === "error") {
    return (
      <ListMessage title={content.title} body={content.body}>
        <Button variant="outline" size="sm" onClick={content.retry.onRetry}>
          {content.retry.label}
        </Button>
      </ListMessage>
    );
  }
  if (content.status === "empty") {
    return <ListMessage title={content.title} body={content.body} />;
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2">
      {content.groups.map((group) => (
        <NotificationGroup key={group.label} group={group} unreadLabel={unreadLabel} />
      ))}
      {content.more && (
        <LoadMore
          shown={content.groups.reduce((sum, group) => sum + group.rows.length, 0)}
          hasMore
          autoLoad
          hideStatus
          loading={content.more.loading}
          label={content.more.label}
          onLoadMore={content.more.onLoadMore}
          className="py-2"
        />
      )}
    </div>
  );
}

function NotificationGroup({
  group,
  unreadLabel,
}: {
  group: NotificationGroupContent;
  unreadLabel?: string;
}) {
  const headingId = useId();
  if (group.rows.length === 0) return null;
  return (
    <section aria-labelledby={headingId}>
      <h3
        id={headingId}
        className="px-4 pt-3 pb-1 font-body text-caption-eyebrow text-text-tertiary uppercase"
      >
        {group.label}
      </h3>
      <ul className="flex flex-col gap-0.5 px-1.5">
        {group.rows.map((row) => (
          <li key={row.id}>
            <NotificationRow notification={row} unreadLabel={unreadLabel} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ListMessage({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <span className="flex size-10 items-center justify-center rounded-pill bg-bg-hover text-text-secondary">
        <Icon icon={bellIcon} size={20} />
      </span>
      <p className="font-body text-body-b1-strong text-text-primary">{title}</p>
      <p className="font-body text-body-b2 text-text-secondary">{body}</p>
      {children}
    </div>
  );
}
